import type { ReactNode } from 'react';

import { isSameDay, subDays } from 'date-fns';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { MonitorEvent } from '@/lib/db/schema';
import { getUptimeColorClasses, getUptimePercentage, UptimePercentage } from '@/lib/utils';

type DailyStatus = {
  date: Date;
  uptimePercent: number;
};

function getDailyData(events: MonitorEvent[] = []): DailyStatus[] {
  const today = new Date();

  const days: DailyStatus[] = [];

  for (let i = 0; i < 60; i++) {
    const day = subDays(today, i);

    const eventsForDay = events.filter((event) => isSameDay(event.checkedAt, day));

    const uptimePercent = getUptimePercentage(eventsForDay);

    days.unshift({
      date: new Date(day),
      uptimePercent,
    });
  }

  return days;
}

type FullWidthClasses = {
  [index: number]: string;
};

const fullWidthClasses: FullWidthClasses = {
  60: 'w-1/78',
  90: 'w-1/115',
};

type Chiclet = {
  date: Date;
  uptimePercent?: number;
  children?: ReactNode;
  className: string;
  omitTooltip?: boolean;
};

function Chiclet({ date, uptimePercent = -1, children, className, omitTooltip = false }: Chiclet) {
  const dateString = new Date(date).toLocaleString('en-US', { month: 'short', day: 'numeric' });
  const tooltip = (
    <div className="flex flex-col px-2 py-1">
      <span className="text-secondary text-xs">{dateString}</span>
      <span className="text-base font-semibold">
        <UptimePercentage uptimePercent={uptimePercent} />
      </span>
    </div>
  );

  return (
    <div
      key={dateString}
      className={`transition-scale relative aspect-2/7 rounded-full duration-180 hover:scale-120 hover:cursor-pointer @max-xl:w-1/60 @max-md:w-1/38 @max-2xs:w-1/19 ${getUptimeColorClasses(uptimePercent).bg} ${className}`}
    >
      {!omitTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="h-full w-full">{children}</div>
          </TooltipTrigger>
          <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        children
      )}
    </div>
  );
}

function StatusHistoryChart({ monitorEvents }: { monitorEvents: MonitorEvent[] }) {
  const dailyData = getDailyData(monitorEvents);
  const totalCount = dailyData.length;

  const chiclets = dailyData.map(({ date, uptimePercent }, index) => {
    const classNames = `
      ${fullWidthClasses[totalCount]}
      ${index < totalCount - 45 ? '@max-xl:hidden' : ''}
      ${index < totalCount - 30 ? '@max-md:hidden' : ''}
      ${index < totalCount - 15 ? '@max-2xs:hidden' : ''}
    `;

    const children =
      index == totalCount - 1 ? (
        <Chiclet
          date={date}
          uptimePercent={uptimePercent}
          omitTooltip={true}
          className="animate-ping-slow-small absolute !h-full !w-full"
        />
      ) : null;

    return (
      <Chiclet
        date={date}
        uptimePercent={uptimePercent}
        className={classNames}
        children={children}
        key={index}
      />
    );
  });

  return (
    <TooltipProvider>
      <div>
        <div className="@container flex justify-between">{chiclets}</div>
        <div className="@container mt-2 flex justify-between text-xs text-gray-500">
          <span className="@2xs:hidden">15d ago</span>
          <span className="@max-2xs:hidden @md:hidden">30d ago</span>
          <span className="@max-md:hidden @xl:hidden">45d ago</span>
          <span className="@max-xl:hidden">{totalCount}d ago</span>
          <span>Today</span>
        </div>
      </div>
    </TooltipProvider>
  );
}

export { StatusHistoryChart };
